<template>
  <div ref="tableWrapRef" class="table-wrap" @scroll="emit('table-scroll')">
    <table class="report-table task-preview-table">
      <thead>
        <tr>
          <th v-for="column in visibleColumns" :key="column.key" :class="`col-${column.key}`">
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="renderedRows.length === 0">
          <td :colspan="visibleColumns.length" class="empty">{{ emptyText || __('No data') }}</td>
        </tr>
        <tr
          v-for="row in renderedRows"
          :key="row.__key"
          :class="{ 'group-separator-row': row.__group }"
        >
          <template v-if="row.__group">
            <td :colspan="visibleColumns.length" class="group-separator-cell">
              <div class="group-separator-content">
                <span class="group-separator-prefix muted">{{ row.prefix }}</span>
                <strong class="group-separator-value">{{ row.value }}</strong>
              </div>
            </td>
          </template>
          <template v-else>
            <td v-for="column in visibleColumns" :key="column.key" :class="`col-${column.key}`">
              <template v-if="column.key === 'task_id'">
                {{ row.__order }}
              </template>
              <template v-else-if="column.key === 'workflow_status_name'">
                <span class="status-indicator">
                  <span
                    class="status-dot"
                    :style="statusDotStyle(row.workflow_status_color)"
                  ></span>
                  <span class="status-name">{{ formatCell(row.workflow_status_name) }}</span>
                </span>
              </template>
              <template v-else-if="column.key === 'priority'">
                <span
                  v-if="row.priority"
                  class="priority-indicator"
                  :title="priorityTooltip(row.priority)"
                >
                  <PriorityIcon :priority="row.priority" :size="16" />
                </span>
                <span v-else class="muted">-</span>
              </template>
              <template v-else-if="column.key === 'title'">
                <a v-if="taskUrl(row)" class="cell-link" :href="taskUrl(row)">
                  {{ formatCell(row.title) }}
                </a>
                <span v-else>{{ formatCell(row.title) }}</span>
              </template>
              <template v-else-if="column.key === 'due_date'">
                <span v-if="row.due_date || row.finished_at" class="date-badge">
                  {{ dueDateText(row) }}
                </span>
                <span v-else class="muted">-</span>
              </template>
              <template v-else-if="column.key === 'project_title'">
                <a v-if="projectUrl(row)" class="cell-link" :href="projectUrl(row)">
                  {{ formatCell(row.project_title) }}
                </a>
                <span v-else>{{ formatCell(row.project_title) }}</span>
              </template>
              <template v-else-if="column.key === 'assignee_name'">
                <span v-if="row.assignee_name" class="name-badge">
                  {{ row.assignee_name }}
                </span>
                <span v-else class="muted">-</span>
              </template>
              <template v-else-if="column.key === 'author_name'">
                <span v-if="row.author_name" class="name-badge">
                  {{ row.author_name }}
                </span>
                <span v-else class="muted">-</span>
              </template>
              <template v-else-if="column.key === 'project_labels'">
                <div class="labels-wrap">
                  <LabelBadge
                    v-for="label in row.project_labels || []"
                    :key="`project-${label.id}`"
                    :label="labelForModal(label, row)"
                    :clickable="clickableLabels"
                    @click-label="emit('label-click', $event)"
                  />
                  <span v-if="!(row.project_labels || []).length" class="muted">-</span>
                </div>
              </template>
              <template v-else-if="column.key === 'personal_labels'">
                <div class="labels-wrap">
                  <LabelBadge
                    v-for="label in row.personal_labels || []"
                    :key="`personal-${label.id}`"
                    :label="label"
                    personal
                    :clickable="clickablePersonalLabels"
                    @click-label="emit('personal-label-click', $event)"
                  />
                  <span v-if="!(row.personal_labels || []).length" class="muted">-</span>
                </div>
              </template>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { __, s__, getLocale, translatePriority } from '@/locale'
import PriorityIcon from '@/components/common/icons/PriorityIcon.vue'
import LabelBadge from '@/components/common/LabelBadge.vue'

const props = defineProps({
  rows: { type: Array, default: () => [] },
  projectScoped: { type: Boolean, default: false },
  grouped: { type: Boolean, default: false },
  swimlaneMode: { type: String, default: '' },
  emptyText: { type: String, default: '' },
  clickableLabels: { type: Boolean, default: false },
  clickablePersonalLabels: { type: Boolean, default: false },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
})

const emit = defineEmits(['table-scroll', 'label-click', 'personal-label-click'])
const tableWrapRef = ref(null)

const taskPreviewColumns = [
  { key: 'task_id', label: '#' },
  { key: 'workflow_status_name', label: s__('ReportConstructor|Workflow') },
  { key: 'priority', label: '' },
  { key: 'title', label: s__('ReportConstructor|Title') },
  { key: 'due_date', label: s__('ReportConstructor|Due date') },
  { key: 'project_title', label: s__('ReportConstructor|Project') },
  { key: 'assignee_name', label: s__('ReportConstructor|Assignee') },
  { key: 'author_name', label: s__('ReportConstructor|Author') },
  { key: 'project_labels', label: s__('ReportConstructor|Project labels') },
  { key: 'personal_labels', label: s__('ReportConstructor|Personal labels') },
]

const locale = computed(() => getLocale())
const visibleColumns = computed(() =>
  taskPreviewColumns.filter(column => !(props.projectScoped && column.key === 'project_title'))
)

const renderedRows = computed(() => {
  if (!props.grouped) {
    return props.rows.map((row, index) => ({
      ...row,
      __group: false,
      __key: `row-${index}-${row.task_id || row.id || row.task_iid || row.iid}`,
      __order: index + 1,
    }))
  }

  const rendered = []
  let previousGroupLabel = null
  let orderNumber = 1

  props.rows.forEach((row, index) => {
    const groupLabel = formatGroupLabel(row)
    if (groupLabel && groupLabel !== previousGroupLabel) {
      const groupPrefix = formatGroupPrefix(row)
      rendered.push({
        __group: true,
        __key: `group-${index}-${groupLabel}`,
        prefix: groupPrefix,
        value: groupLabel,
      })
      previousGroupLabel = groupLabel
    }

    rendered.push({
      ...row,
      __group: false,
      __key: `row-${index}-${row.task_id || row.id || row.task_iid || row.iid}`,
      __order: orderNumber,
    })
    orderNumber += 1
  })

  return rendered
})

function dueDateText(row) {
  const sourceValue = row.due_date || row.finished_at
  if (!sourceValue) return '-'
  const date = new Date(sourceValue)
  if (Number.isNaN(date.getTime())) return '-'
  const formatterLocale = locale.value === 'ru' ? 'ru-RU' : 'en-US'
  return new Intl.DateTimeFormat(formatterLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

function taskUrl(row) {
  if (!row.project_id) return ''

  const taskSegment = row.task_iid || row.iid
  if (!taskSegment) return ''

  const url = new URL(
    `/projects/${encodeURIComponent(row.project_id)}/tasks/${encodeURIComponent(taskSegment)}`,
    window.location.origin
  )

  if (props.chatId) url.searchParams.set('chat_id', props.chatId)
  if (props.fromId) url.searchParams.set('from_id', props.fromId)

  return `${url.pathname}${url.search}`
}

function projectUrl(row) {
  if (!row.project_id) return ''
  return `/projects/${encodeURIComponent(row.project_id)}/dashboard`
}

function labelForModal(label, row) {
  return {
    ...label,
    projectId: label.projectId || label.project_id || row.project_id,
  }
}

function statusDotStyle(color) {
  return { backgroundColor: color || '#9ca3af' }
}

function priorityTooltip(priority) {
  if (!priority) return s__('ReportConstructor|No priority')
  return translatePriority(priority)
}

function formatCell(value) {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

function formatUserGroupLabel(username, fullName, fallback) {
  const normalizedUsername = username ? String(username).trim() : ''
  const normalizedName = fullName ? String(fullName).trim() : ''

  if (normalizedUsername && normalizedName && normalizedUsername !== normalizedName) {
    return `${normalizedUsername} (${normalizedName})`
  }
  if (normalizedUsername) return normalizedUsername
  if (normalizedName) return normalizedName
  return fallback || ''
}

function swimlaneModeLabel(swimlaneMode) {
  const modeLabels = {
    by_project: s__('Boards|By Project'),
    by_author: s__('Boards|By Author'),
    by_assignee: s__('Boards|By Assignee'),
    by_category: s__('Boards|By Category'),
    by_status: s__('Boards|By Status'),
    by_due_date: s__('Boards|By Due Date'),
    by_priority: s__('Boards|By Priority'),
  }
  return modeLabels[swimlaneMode] || s__('ReportConstructor|by swimlane')
}

function formatGroupLabel(row) {
  const groupLabel = row.group_label || row.group_value || row.bucket || row.swimlane
  if (!groupLabel) return ''

  const labelKey = String(groupLabel)

  if (row.group_kind === 'swimlane') {
    if (props.swimlaneMode === 'by_project') {
      return row.project_title || s__('ReportConstructor|No project')
    }
    if (props.swimlaneMode === 'by_assignee') {
      if (labelKey === 'unassigned') return s__('TaskFilter|Unassigned')
      return formatUserGroupLabel(
        row.assignee_username,
        row.assignee_full_name,
        row.assignee_name || labelKey
      )
    }
    if (props.swimlaneMode === 'by_author') {
      if (labelKey === 'no_author') return s__('ReportConstructor|No author')
      return formatUserGroupLabel(
        row.author_username,
        row.author_full_name,
        row.author_name || labelKey
      )
    }
    if (props.swimlaneMode === 'by_category') {
      if (labelKey === 'no_category') return s__('ReportConstructor|No category')
      return row.task_category_name || labelKey
    }
    if (props.swimlaneMode === 'by_status') {
      if (labelKey === 'no_status') return s__('ReportConstructor|No status')
      return row.workflow_status_name || labelKey
    }
    if (props.swimlaneMode === 'by_due_date') {
      if (labelKey === 'overdue') return s__('Dashboard|Overdue')
      if (labelKey === 'today') return s__('Dashboard|Due today')
      if (labelKey === 'upcoming') return s__('Dashboard|Upcoming')
      if (labelKey === 'no_due_date' || labelKey === 'no_due') return s__('Dashboard|No due date')
      if (labelKey === 'finished') return s__('Dashboard|Finished')
      if (labelKey === 'archived') return s__('TaskRow|Archived')
      return labelKey
    }
  }

  const groupMap = {
    overdue: s__('Dashboard|Overdue'),
    today: s__('Dashboard|Due today'),
    upcoming: s__('Dashboard|Upcoming'),
    no_due: s__('Dashboard|No due date'),
    unfinished: s__('Report|My Unfinished Tasks'),
    completed: s__('Report|Previous Week'),
    no_due_date: s__('Dashboard|No due date'),
    finished: s__('Dashboard|Finished'),
    archived: s__('TaskRow|Archived'),
    unassigned: s__('Report|Unassigned'),
    no_project: s__('ReportConstructor|No project'),
    no_author: s__('ReportConstructor|No author'),
    no_status: s__('ReportConstructor|No status'),
    no_category: s__('ReportConstructor|No category'),
    no_priority: s__('ReportConstructor|No priority'),
    highest: translatePriority('highest'),
    high: translatePriority('high'),
    medium: translatePriority('medium'),
    low: translatePriority('low'),
    lowest: translatePriority('lowest'),
  }
  return groupMap[labelKey] || labelKey
}

function formatGroupPrefix(row) {
  const groupLabel = String(row.group_label || row.group_value || row.bucket || row.swimlane || '')
  const priorityValues = new Set(['highest', 'high', 'medium', 'low', 'lowest', 'no_priority'])
  let criterion = s__('ReportConstructor|group')

  if (row.group_kind === 'bucket') {
    criterion = s__('ReportConstructor|by urgency')
  } else if (priorityValues.has(groupLabel)) {
    criterion = s__('ReportConstructor|by priority')
  } else if (row.group_kind === 'scope') {
    criterion = s__('ReportConstructor|by scope')
  } else if (row.group_kind === 'swimlane') {
    criterion = swimlaneModeLabel(props.swimlaneMode)
  } else if (groupLabel === 'no_due_date' || /^\d{4}-\d{2}-\d{2}$/.test(groupLabel)) {
    criterion = s__('ReportConstructor|by due date')
  }

  return `${s__('ReportConstructor|Grouping criteria')}: ${criterion} - `
}

defineExpose({
  tableWrapElement: tableWrapRef,
})
</script>
